/* eslint-disable @next/next/no-img-element */
import React, { useEffect, useState } from 'react';
import cx from 'classnames';
import Image from 'next/image';
import { Gallery, Item } from 'react-photoswipe-gallery';
import { twMerge } from 'tailwind-merge';
import 'photoswipe/dist/photoswipe.css';

import { BUTTON_TYPES } from 'constant';
import { AccessoriesDataItemInterface } from '@/types/api';
import useCart from '@/hooks/cart';

import Counter from '../Counter';
import PriceBox from '../PriceBox';
import Button from '../Button';
import styles from './AccessoryInner.module.scss';
import Tag from '../Tag';

const AccessoryInner = ({
  id,
  title,
  description,
  photos,
  weight,
  price,
  length,
  height,
  width,
  discountedPrice,
}: AccessoriesDataItemInterface): JSX.Element => {
  const { addItemToCart, isLoading } = useCart();

  const [quantity, setQuantity] = useState(1);
  const [mainImage, setMainImage] = useState(photos?.[0]?.url);
  const [isAddingToCart, setIsAddingToCart] = useState(false);

  const handleAddToCart = () => {
    setIsAddingToCart(true);

    addItemToCart('accessory', quantity, id);
  };

  const showGallery = photos.length > 3;

  useEffect(() => {
    if (!isLoading && isAddingToCart) setIsAddingToCart(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  return (
    <div className="bg-white">
      <div className={cx(styles.container, 'grid mx-auto w-fit')}>
        <div className={styles.photosContainer}>
          <div className="w-full aspect-square max-h-[588px] mb-8">
            <Image
              loader={() => `${mainImage}?w=588&h=588`}
              src={mainImage}
              alt={`${title} image`}
              width={588}
              height={588}
              priority
              className="rounded-lg self-center h-full object-contain mx-auto"
            />
          </div>
          {photos.length > 1 && (
            <Gallery>
              <div className={cx(styles.photoGaleryOptionsContainer, 'grid')}>
                {photos.map(({ id, url }, index) => {
                  const photoUrl = url;

                  const imageContent = (
                    <div className="w-full h-full grid items-center justify-items-center">
                      <div className="w-full h-full max-w-[90vw] max-h-[90vh] flex items-center justify-items-center">
                        <Image
                          key={id}
                          loader={() => `${photoUrl}?w=1920&h=1080`}
                          src={photoUrl}
                          alt={`${title} image`}
                          width={1920}
                          height={1080}
                          className="!max-w-full max-h-full object-contain"
                        />
                      </div>
                    </div>
                  );

                  if (showGallery && index === 2) {
                    return (
                      <Item key={id} content={imageContent}>
                        {({ ref, open }) => (
                          <div
                            className="relative aspect-square cursor-pointer"
                            onClick={open}
                          >
                            <div className="absolute w-full h-full bg-white/50" />
                            <div className="absolute bottom-0 right-0 bg-red-500 pl-3 pr-[6px] h-8 grid justify-center items-center rounded-tl-full text-white">
                              +{photos.length - 3}
                            </div>
                            <img
                              src={photoUrl}
                              ref={
                                ref as React.MutableRefObject<HTMLImageElement>
                              }
                              className="rounded-lg w-full aspect-square object-cover"
                              alt=""
                            />
                          </div>
                        )}
                      </Item>
                    );
                  }

                  return (
                    <div
                      className={cx('cursor-pointer aspect-square', {
                        hidden: index > 2,
                      })}
                      key={id}
                    >
                      <Item content={imageContent}>
                        {({ ref, open }) => (
                          <img
                            src={photoUrl}
                            ref={
                              ref as React.MutableRefObject<HTMLImageElement>
                            }
                            onClick={open}
                            className="hidden"
                            alt=""
                          />
                        )}
                      </Item>
                      {(!showGallery || index < 2) && (
                        <Image
                          loader={() => `${photoUrl}?w=0&h=0`}
                          src={photoUrl}
                          className={twMerge(
                            cx(
                              'rounded-lg w-full aspect-square object-cover border-gray-700 border',
                              { 'border-brand-100': mainImage === photoUrl },
                            ),
                          )}
                          alt={`${title} image`}
                          width={0}
                          height={0}
                          onClick={() => setMainImage(photoUrl)}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </Gallery>
          )}
        </div>
        <div className={cx(styles.infoContainer, 'flex flex-col gap-y-8')}>
          <h1 className="font-medium text-2xl text-brand-100">{title}</h1>
          <hr className="border-gold-50 border-1" />
          <PriceBox
            price={price}
            discount={discountedPrice}
            priceStyle="text-2xl"
          />
          <div className="flex sm:flex-row flex-col gap-x-3 gap-y-3">
            <Tag
              icon="check"
              title="Part Available"
              iconColor="text-gray-muted"
              color="text-lime-700"
            />
            <Tag
              icon="check"
              title="Includes VAT"
              iconColor="text-gray-muted"
              color="text-lime-700"
            />
            <Tag
              icon="add"
              title="Excludes Shipping"
              iconColor="text-gray-muted"
              color="text-gray-muted"
              underlined
            />
          </div>
          <div
            className={cx(
              'text-[#5A5A5A] text-base font-normal',
              styles.descriptionBox,
            )}
          >
            <ul className="list-disc ml-3 text-base">
              <li className="p-2">
                <div className="mr-1 font-medium">Condition</div>
                <div>100% new and genuine</div>
              </li>
              <li className="p-2">
                <div className="mr-1 font-medium">Weight</div>
                <div>{weight} kg</div>
              </li>
              <li>
                <ul className="flex flex-row">
                  <li className="p-2">
                    <div className="mr-1 font-medium">Length</div>
                    <div>{length} cm</div>
                  </li>
                  <li className="p-2">
                    <div className="mr-1 font-medium">Width</div>
                    <div>{width} cm</div>
                  </li>
                  <li className="p-2">
                    <div className="mr-1 font-medium">Height</div>
                    <div>{height} cm</div>
                  </li>
                </ul>
              </li>
            </ul>
            <br />
            {description}
          </div>
          <div className="flex flex-row items-center gap-x-8">
            <Counter
              count={quantity}
              minCount={1}
              setCount={setQuantity}
              disabled={isAddingToCart}
            />
            <Button
              className={cx(styles.addToCartBtn, 'w-full h-[52px]')}
              type={BUTTON_TYPES.FILLED}
              onClick={handleAddToCart}
              loading={isAddingToCart}
            >
              Add to cart
            </Button>
          </div>
          <div className="mt-8">
            <hr className="border-gray-900 border-1 mb-6" />
            <div className="pr-12">
              <div className="text-brand-100 text-lg font-medium mb-2">
                Delivery
              </div>
              <p className="font-normal text-base text-[#5A5A5A]">
                Usually, delivery will take 3 to 4 business days, depending on
                part you order and delivery address. But if ordered part is on
                Germany back order it usually takes 5-7 business days or longer.
                Please check our Delivery policy.
              </p>
            </div>
            <hr className="border-gray-900 border-1 mt-6" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AccessoryInner;
